import { useRef, useState, useEffect, type CSSProperties, type KeyboardEvent, type ReactNode } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Button as PennyButton,
  Checkbox,
  Container,
  Form,
  FormField,
  Group,
  PhoneField,
  SelectNew,
  Text,
  TextArea,
  TextField,
  useMelioForm,
} from '@melio/penny';

import { useApp, Button, Banner, Icon, IconButton, Illustration, MelioLogo } from '@/components/ds';
import { IndustryField, AddressFields } from '@/components/form';
import { onboardOrExistingPartner, ensureEntityForEnv } from '@/api/portal';
import { trackEvent, trackPartnerView, trackPartnerClick, useFlowAnalytics } from '@/lib/analytics';
import { GATE_ENV, HOST_ENV, ONBOARDING_ENVS } from '@/config/environments';
import {
  emptyAddress,
  businessTypeOptions,
  taxTypeOptionsFor,
  taxTypeLabels,
  taxIdMaskFor,
  coerceTaxTypeForBusinessType,
  mkAddress,
  buildEntityBody,
  ownerSpecs,
  businessInfoSpecs,
  legalInfoSpecs,
  legalAddressSpecs,
  addressSpecs,
  validateSpecs,
  entityFieldErrorFrom,
  type EntityFieldError,
  type FieldSpec,
} from '@/lib/entityForm';
import type { Go } from '@/lib/types';

// onboarding.tsx — post-login onboarding for a signed-in (auth-service) user who has no partner yet.
// Flow (each a top-level milestone, like the welcome flow): usecase (beta application) → owner (business
// owner, who also doubles as the account's point of contact) → business (profile) → legal (business type
// & tax) → address (legal + operating, one milestone with an inner Legal/Operating sub-step) → done. On
// submit the whole form is wrapped into a beta application
// and sent for vetting (auto-approved today); once approved the backend creates/reuses the partner and
// issues a key in every provisioning env (staging01 + production), and the KYB business entity is then
// created client-side per env (it needs a browser Sonar token). The PennyProvider/IntlProvider wrapping
// is applied app-wide in lib/penny.

type Step = 'usecase' | 'owner' | 'business' | 'legal' | 'address' | 'review' | 'done';

// Partner-View/Partner-Click PageName + Intent per wizard step (the address step's two sub-steps get
// their own entries). PageName is the kebab-case slug of that step's on-screen title (StepHeader).
type StepKey = 'usecase' | 'owner' | 'business' | 'legal' | 'address-legal' | 'address-operating' | 'review';
const STEP_META: Record<StepKey, { pageName: string; intent: string }> = {
  usecase: { pageName: 'how-will-you-use-the-api', intent: 'choose-api-integration' },
  owner: { pageName: 'business-owner', intent: 'business-owner-details' },
  business: { pageName: 'tell-us-about-your-business', intent: 'business-details' },
  legal: { pageName: 'legal-information', intent: 'fill-legal-details' },
  'address-legal': { pageName: 'legal-registration-address', intent: 'fill-legal-address' },
  'address-operating': { pageName: 'operating-address', intent: 'fill-operating-address' },
  review: { pageName: 'review-and-create', intent: 'submit-partner-details' },
};
const stepKeyFor = (step: Step, addressSub: number): StepKey =>
  step === 'address' ? (addressSub === 0 ? 'address-legal' : 'address-operating') : (step as StepKey);

// "Which describes your integration?" — mirrors the beta application form's audience question.
const buildingForOptions = [
  { value: 'own', label: 'Direct', description: "We're paying our own vendors and contractors." },
  {
    value: 'platform',
    label: 'Platform',
    description: "We're building a platform so other businesses can pay their vendors and contractors.",
  },
];

// Validation spec lists — defined at module level (no closure deps) so refs stay stable. The entity
// fields' specs are shared with the dashboard's "Add entity" page (lib/entityForm.ts). The owner is
// also the account's point of contact (there's no separate "about you" step; the owner's name doubles
// as `contact.firstName/lastName` on the entity — see buildEntityBody). `ownerEmail` is prefilled from
// the signed-in email once, then editable; it's also the address the beta application records, so the
// owner step's email check is what guarantees `/onboard` never receives an application without a valid one.
const useCaseSpecs: FieldSpec[] = [
  { name: 'buildingFor', label: 'Which describes your integration', message: 'Select which describes your integration' },
  { name: 'projectDescription', label: 'What you’re building' },
];

// The title + intro copy every wizard step opens with. `as="h2"` keeps the heading role the
// e2e suite (and any screen-reader user) navigates by; penny's type scale supplies the rest.
function StepHeader({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <Container paddingBottom="l">
      <Text as="h2" textStyle="heading2Semi">
        {title}
      </Text>
      {children && (
        <Container paddingTop="xs">
          <Text textStyle="body2" color="semantic.text.secondary">
            {children}
          </Text>
        </Container>
      )}
    </Container>
  );
}

// Normalize a phone to a leading-+ form when we can (US 10/11 digit); otherwise leave as typed.
function normPhone(v: any): string {
  const raw = String(v ?? '').trim();
  if (!raw) return '';
  if (raw.startsWith('+')) return raw.replace(/[^\d+]/g, '');
  const d = raw.replace(/\D/g, '');
  if (d.length === 10) return '+1' + d;
  if (d.length === 11 && d.startsWith('1')) return '+' + d;
  return raw;
}

// The legal notice lives at the foot of the form panel (a light surface), so these read as
// ordinary body links rather than the inverse treatment the dark brand panel needed.
const LEGAL_LINK: CSSProperties = { color: 'var(--text-primary)', textDecoration: 'underline' };

// Support inbox surfaced to the user when submission fails for a reason we didn't anticipate — a 5xx,
// a network/CORS/timeout failure (no `status` at all), or anything else not covered below. A 4xx from
// the backend (PortalError/ApiError, both carry `.status`) means the server understood the request and
// already explained what's wrong with it (bad input, a business-rule conflict, ...), so its own message
// is enough; same for the two messages createPartner throws itself for conditions the user can act on
// directly (re-sign-in, nothing configured to provision into).
const SUPPORT_EMAIL = 'erez.shasha@xero.com';
const KNOWN_SUBMIT_ERRORS = new Set(['Your session has expired. Please sign in again.', 'No environment is configured for provisioning.']);
const isClientError = (ex: any): boolean => typeof ex?.status === 'number' && ex.status >= 400 && ex.status < 500;

// Shown under the form on every step: the same notice the welcome page gives before it takes any
// business details. It sits with the form rather than on the brand panel because it qualifies the
// Continue button the user is about to press.
function LegalNotice() {
  return (
    <Text as="p" textStyle="caption1" color="semantic.text.secondary">
      By continuing, you accept Melio&apos;s{' '}
      <a href="https://meliopayments.com/privacy-policy/" target="_blank" rel="noreferrer" style={LEGAL_LINK}>
        Privacy Policy
      </a>{' '}
      and{' '}
      <a href="https://developers.melio.com/api-agreement" target="_blank" rel="noreferrer" style={LEGAL_LINK}>
        API Agreement
      </a>
      . Payment services are subject to Melio&apos;s{' '}
      <a href="https://meliopayments.com/terms-of-service/" target="_blank" rel="noreferrer" style={LEGAL_LINK}>
        Terms of Service
      </a>
      .
    </Text>
  );
}

// One label/value row in the review summary (label left, value right, bottom rule).
function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <Container paddingY="xs" style={{ borderBottom: '1px solid var(--border-static)' }}>
      <Group variant="horizontal" justifyContent="space-between" spacing="m" alignItems="baseline">
        <Text textStyle="body4" color="semantic.text.secondary">
          {label}
        </Text>
        <Text textStyle="body4Semi" style={{ textAlign: 'right' }}>
          {value || '—'}
        </Text>
      </Group>
    </Container>
  );
}

interface KeyRevealProps {
  apiKey: string;
  onContinue?: () => void;
  continueLabel?: string;
}

// The key itself plus the copy/mask controls and the "treat this like a password" warning —
// everything that is true wherever a freshly-minted key is shown. The surrounding chrome differs
// by context, so it lives with the caller: the onboarding step wraps this in a badge + heading +
// a full-width continue button, while the dashboard shows it in a drawer that already supplies
// both a header and a footer.
function ApiKeyPanel({ apiKey }: { apiKey: string }) {
  const [masked, setMasked] = useState(false);
  const [copied, setCopied] = useState(false);
  const display = masked ? apiKey.slice(0, 8) + '\u2022'.repeat(apiKey.length - 8) : apiKey;
  const copy = () => {
    try {
      navigator.clipboard.writeText(apiKey);
    } catch (e) {}
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };
  return (
    <>
      <Banner kind="warning" style={{ marginBottom: 16 }}>
        <strong>This is the only time we&apos;ll show it in full.</strong>{' '}
        <strong>Copy it now and store it somewhere safe — like a secrets manager.</strong>
      </Banner>
      {/* Deliberately a dark "secret" surface, so the colors here are literal rather than tokens —
          penny has no dark-surface container variant. The controls on it use penny's inverse variants. */}
      <div
        style={{
          background: '#16171f',
          borderRadius: 12,
          border: '1px solid #2c2f3d',
          padding: 16,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Icon name="lock" size={16} color="#5c6472" />
        <code className="om-mono" style={{ flex: 1, fontSize: 14, color: '#8fd6a8', letterSpacing: '.01em', wordBreak: 'break-all' }}>
          {display}
        </code>
        <IconButton
          name={masked ? 'show' : 'hide'}
          ariaLabel={masked ? 'Show the full key' : 'Hide the key'}
          variant="naked-inverse"
          size={32}
          onClick={() => setMasked((m) => !m)}
        />
        <PennyButton
          size="small"
          variant={copied ? 'success' : 'primary'}
          label={copied ? 'Copied' : 'Copy'}
          leftElement={<Icon name={copied ? 'checked' : 'copy'} size={15} />}
          onClick={copy}
        />
      </div>
      <Banner kind="warning" style={{ marginTop: 16 }}>
        Treat this like a password. Anyone with this key can move money on your account. Never expose it in a browser or commit it to git.
      </Banner>
    </>
  );
}

// The onboarding step's presentation of a new key: the lock badge and heading that give it weight
// as a milestone in the wizard, and a full-width button on to the next thing.
function KeyReveal({ apiKey, onContinue, continueLabel = 'Continue to dashboard' }: KeyRevealProps) {
  return (
    <div style={{ animation: 'om-fade-up .3s ease' }}>
      <Container width="fit-content" paddingX="s" paddingY="s" backgroundColor="light" style={{ borderRadius: 14, marginBottom: 18 }}>
        <Icon name="lock" size={26} color="var(--accent)" />
      </Container>
      <Container paddingBottom="xs">
        <Text as="h2" textStyle="heading2Semi">
          Your secret API key
        </Text>
      </Container>
      <ApiKeyPanel apiKey={apiKey} />
      <Button fullWidth size="large" style={{ marginTop: 22 }} rightIcon="arrow-right" onClick={onContinue}>
        {continueLabel}
      </Button>
    </div>
  );
}

interface OnboardingProps {
  go: Go;
}

function Onboarding({ go }: OnboardingProps) {
  const app = useApp();
  // Boots FullStory + Segment for this flow and tags the session (see lib/analytics.ts). Skips the
  // vendor's default 'onboarding-Viewed' event — Partner-View below already covers every step view.
  useFlowAnalytics('onboarding', { skipViewEvent: true });

  // Partner-View/Partner-Click for the wizard (see STEP_META) — every step view and every button click,
  // with the signed-in user's id/email attached from the app context.
  const viewStep = (key: StepKey) => {
    const meta = STEP_META[key];
    trackPartnerView({ userId: app.userId, userEmail: app.email, pageName: meta.pageName, intent: meta.intent });
  };
  const clickStep = (key: StepKey, cta: string, values?: Record<string, unknown>) => {
    const meta = STEP_META[key];
    trackPartnerClick({ userId: app.userId, userEmail: app.email, pageName: meta.pageName, intent: meta.intent, cta, values });
  };

  // The address step has an inner sub-step: 0 = legal registration, 1 = operating (mirrors the welcome flow).
  const [step, setStep] = useState<Step>('usecase');
  const [addressSub, setAddressSub] = useState(0);

  // Funnel entry: fire the first step's Partner-View once when a not-yet-onboarded user actually starts
  // the wizard (skipped for the already-onboarded user who gets bounced to /dashboard below).
  const startedRef = useRef(false);
  useEffect(() => {
    if (startedRef.current || app.onboarded === true) return;
    startedRef.current = true;
    viewStep('usecase');
  }, [app.onboarded]);

  const [submitting, setSubmitting] = useState(false);
  const [submitErr, setSubmitErr] = useState('');
  // Set only on an unexpected createPartner failure (see KNOWN_SUBMIT_ERRORS) — tells the review step
  // to show the "contact us" note alongside submitErr.
  const [submitErrUnexpected, setSubmitErrUnexpected] = useState(false);
  // What provisioning is currently doing. Surfaced under the submit button because the work is
  // several network round-trips per environment and can run for a while — long enough that a bare
  // spinner reads as stalled.
  const [submitPhase, setSubmitPhase] = useState('');

  // Already-onboarded users don't belong in the wizard — bounce once on mount. (The route guard only
  // checks auth, so that marking the user onboarded at the end doesn't yank them off the key reveal.)
  const bouncedRef = useRef(false);
  useEffect(() => {
    if (!bouncedRef.current && app.onboarded === true) {
      bouncedRef.current = true;
      go('/dashboard');
    }
  }, [app.onboarded, go]);

  // Refs for re-validation subscription (avoid stale closures)
  const stepAttemptedRef = useRef(false);
  const currentSpecsRef = useRef<FieldSpec[]>([]);

  // Every step change is its own browser-history entry (the step rides in the entry's state, the URL
  // stays /onboarding), so the browser's Back/Forward move through the wizard. Without this the whole
  // wizard is a single entry, and Back leaves it for the accounts sign-up screens it was reached from.
  const navigate = useNavigate();
  const location = useLocation();
  const recordPosition = (s: Step, sub: number, replace = false) =>
    navigate(`${window.location.pathname}${window.location.search}`, { replace, state: { onboardingStep: s, addressSub: sub } });
  useEffect(() => {
    if (app.onboarded !== true) recordPosition('usecase', 0, true);
  }, []);

  const goStep = (s: Step) => {
    // 'done' isn't a StepHeader screen and has no STEP_META entry — don't track a Partner-View for it.
    if (s !== 'done') viewStep(stepKeyFor(s, 0));
    recordPosition(s, 0);
    setStep(s);
    setAddressSub(0);
    setSubmitErr('');
    setSubmitErrUnexpected(false);
    stepAttemptedRef.current = false;
  };
  const goAddressSub = (n: number) => {
    viewStep(stepKeyFor('address', n));
    recordPosition('address', n);
    setAddressSub(n);
    setSubmitErr('');
    setSubmitErrUnexpected(false);
    stepAttemptedRef.current = false;
  };

  const form: any = useMelioForm({
    defaultValues: {
      buildingFor: '',
      projectDescription: '',
      // Business owner — also the point of contact; ownerEmail is prefilled from the logged-in email
      ownerFirst: '',
      ownerLast: '',
      ownerEmail: '',
      ownerPhone: '',
      ownerDob: null,
      legalName: '',
      industry: undefined,
      phone: '',
      website: '',
      businessType: 'llc',
      taxIdType: 'ein',
      taxId: '',
      description: '',
      address: { ...emptyAddress },
      legalAddress: { ...emptyAddress },
      operatingSameAsLegal: true,
    },
    onSubmit: () => {
      if (step === 'address' && addressSub === 1) goToReview();
      else if (step === 'review') createPartner();
    },
  });
  const { formProps, registerField, watch, getValues, setValue, setError, clearErrors } = form;

  const taxIdType = watch('taxIdType');
  const businessType = watch('businessType');
  const operatingSameAsLegal = watch('operatingSameAsLegal');

  const taxTypeOptions = taxTypeOptionsFor(businessType);

  // Keep the tax id type valid for the chosen formation type (see taxTypesForBusinessType).
  useEffect(() => {
    setValue('taxIdType', coerceTaxTypeForBusinessType(businessType, getValues().taxIdType));
  }, [businessType]);

  // Keep currentSpecsRef in sync with the active step/substep so the watch
  // subscription below (set up once) can re-validate without stale closures.
  useEffect(() => {
    if (step === 'usecase') currentSpecsRef.current = useCaseSpecs;
    else if (step === 'owner') currentSpecsRef.current = ownerSpecs;
    else if (step === 'business') currentSpecsRef.current = businessInfoSpecs;
    else if (step === 'legal') currentSpecsRef.current = legalInfoSpecs;
    else if (step === 'address') currentSpecsRef.current = addressSub === 0 ? legalAddressSpecs : operatingSameAsLegal ? [] : addressSpecs;
    else currentSpecsRef.current = [];
  }, [step, addressSub, operatingSameAsLegal]);

  // Validate on change after the step has been attempted (Continue clicked).
  // This is the "re-validate on change after field is visited" behavior.
  useEffect(() => {
    const sub = watch(() => {
      if (!stepAttemptedRef.current) return;
      const specs = currentSpecsRef.current;
      if (!specs.length) return;
      // Re-evaluate only the current step's fields.
      setSubmitErr(validateSpecs(specs, getValues(), setError, clearErrors));
    });
    return () => sub?.unsubscribe?.();
  }, []);

  // Validate a set of specs, set field-level errors, return first message.
  const checkRequired = (specs: FieldSpec[]) => validateSpecs(specs, getValues(), setError, clearErrors);

  // Advance helper: validate specs and proceed; marks step as attempted for re-validation.
  const advance = (specs: FieldSpec[] | (() => FieldSpec[]), callback: () => void) => {
    stepAttemptedRef.current = true;
    const err = checkRequired(typeof specs === 'function' ? specs() : specs);
    if (err) {
      setSubmitErr(err);
      return;
    }
    setSubmitErr('');
    stepAttemptedRef.current = false;
    callback();
  };

  // Prefill the owner's email once from the signed-in email; the field stays editable for the case
  // where the owner is someone other than the person filling this out.
  const ownerEmailPrefilledRef = useRef(false);
  const goToOwner = () =>
    advance(useCaseSpecs, () => {
      if (!ownerEmailPrefilledRef.current) {
        ownerEmailPrefilledRef.current = true;
        setValue('ownerEmail', app.email ?? '');
      }
      goStep('owner');
    });
  const goToBusiness = () =>
    advance(ownerSpecs, () => {
      goStep('business');
    });

  const goToLegal = () =>
    advance(businessInfoSpecs, () => {
      goStep('legal');
    });
  const goToAddress = () =>
    advance(legalInfoSpecs, () => {
      goStep('address');
    });
  const goToOperating = () =>
    advance(legalAddressSpecs, () => {
      goAddressSub(1);
    });
  // From the operating sub-step, validate the address(es) then move to the review summary.
  const goToReview = () =>
    advance(operatingSameAsLegal ? legalAddressSpecs : [...legalAddressSpecs, ...addressSpecs], () => {
      goStep('review');
    });

  // Partner-Click wrappers: fire the click (attributed to the page the button lives on), then run the
  // existing navigation/validation. Wired to both the button's onClick and the Enter-key handler, so
  // either input method reports the same click.
  const onContinueUsecase = () => {
    clickStep('usecase', 'continue', { integration_type: getValues().buildingFor });
    goToOwner();
  };
  const onBackFromOwner = () => {
    clickStep('owner', 'back');
    goStep('usecase');
  };
  const onContinueFromOwner = () => {
    clickStep('owner', 'continue');
    goToBusiness();
  };
  const onBackFromBusiness = () => {
    clickStep('business', 'back');
    goStep('owner');
  };
  const onContinueFromBusiness = () => {
    clickStep('business', 'continue', { BusinessName: getValues().legalName });
    goToLegal();
  };
  const onBackFromLegal = () => {
    clickStep('legal', 'back');
    goStep('business');
  };
  const onContinueFromLegal = () => {
    clickStep('legal', 'continue');
    goToAddress();
  };
  const onBackFromAddressLegal = () => {
    clickStep('address-legal', 'back');
    goStep('legal');
  };
  const onContinueFromAddressLegal = () => {
    clickStep('address-legal', 'continue');
    goToOperating();
  };
  const onBackFromAddressOperating = () => {
    clickStep('address-operating', 'back');
    goAddressSub(0);
  };
  const onContinueFromAddressOperating = () => {
    clickStep('address-operating', 'continue', { SameAsLegalAddress: getValues().operatingSameAsLegal });
    goToReview();
  };
  const onBackFromReview = () => {
    clickStep('review', 'back');
    setStep('address');
    goAddressSub(1);
  };

  // Final submit: provision the partner + keys (backend), then create the KYB business entity in each
  // env with the issued key (client-side, for the Sonar token). Idempotent/resumable server-side.
  // Re-entrancy guard. Penny's loading button sets `pointer-events: none`, which stops the mouse
  // but not the keyboard — the button stays focusable, and this step also submits on Enter (see
  // the form's onSubmit). Provisioning is several non-trivial calls per environment, so a second
  // concurrent run is worth refusing outright. A ref rather than the `submitting` state because
  // this closure would otherwise read a stale value.
  const submittingRef = useRef(false);
  const createPartner = async () => {
    if (submittingRef.current) return;
    // Re-check every step, not just the last: browser Forward can reach the review step past a step
    // whose fields were changed after it was last validated. Send the user to the first one that fails.
    const stepSpecs: [Step, number, FieldSpec[]][] = [
      ['usecase', 0, useCaseSpecs],
      ['owner', 0, ownerSpecs],
      ['business', 0, businessInfoSpecs],
      ['legal', 0, legalInfoSpecs],
      ['address', 0, legalAddressSpecs],
      ['address', 1, operatingSameAsLegal ? [] : addressSpecs],
    ];
    for (const [s, sub, specs] of stepSpecs) {
      if (!checkRequired(specs)) continue;
      if (sub === 0) goStep(s);
      else {
        setStep(s);
        goAddressSub(sub);
      }
      // goStep clears the banner and the attempted flag; restore both for the step just shown.
      stepAttemptedRef.current = true;
      setSubmitErr(checkRequired(specs));
      return;
    }
    submittingRef.current = true;
    setSubmitting(true);
    setSubmitErr('');
    setSubmitErrUnexpected(false);
    // Two round-trips per environment (provision the partner, then create the KYB entity), so the
    // phase counter is over that total rather than over the environment list.
    const totalPhases = ONBOARDING_ENVS.length * 2;
    let phase = 0;
    const reportPhase = (label: string) => setSubmitPhase(`${label} (step ${++phase} of ${totalPhases})`);
    try {
      const v = getValues();
      const email = app.email ?? '';
      const legalAddr = mkAddress(v.legalAddress);
      const entityBody = buildEntityBody(v);

      // The beta application (use case + business details) is sent to `/onboard`, which vets it server-side
      // and holds the flow on a decline before the partner is created — auto-approved for now, so there's
      // no client-side gate here. `email` is the business owner's, collected on the owner step: the access
      // token carries no email claim (auth-service puts it only in the id_token), so the server can't fill one.
      const application = {
        email: v.ownerEmail,
        companyName: v.legalName,
        website: v.website || undefined,
        companyAddress: [legalAddr.line1, legalAddr.city, legalAddr.state, legalAddr.postalCode].filter(Boolean).join(', ') || undefined,
        industry: v.industry?.name || v.industry?.naicsCode || undefined,
        buildingFor: v.buildingFor,
        projectDescription: v.projectDescription,
      };

      // Provision the partner in each env by calling that env's portal API directly — one call per env,
      // no server-side cross-env fan-out — then create the KYB entity in that env with the user's access
      // token (never the api-key; Sonar is minted from the partner's non-secret sonarKey inside the helper).
      // The env this portal is served from (HOST_ENV) is the one that must succeed — it holds the
      // user's own partner and answers the dashboard. Any *other* env in the list is a secondary
      // convenience (production also provisions a staging01 sandbox partner), and it is reached with an
      // access token minted by this env's auth-service, which the other env may reject. Let a secondary
      // env fail without taking the whole wizard down; the partner can be backfilled there later.
      let primaryPartnerName: string | undefined;
      const skippedEnvs: string[] = [];
      for (const env of ONBOARDING_ENVS) {
        const token = await app.getAccessToken();
        if (!token) throw new Error('Your session has expired. Please sign in again.');

        try {
          reportPhase('Creating your account');
          const { partnerName, sonarKey } = await onboardOrExistingPartner(env, token, application);
          reportPhase('Setting up your business');
          await ensureEntityForEnv(env, app.getAccessToken, sonarKey, entityBody);
          if (env === GATE_ENV || primaryPartnerName === undefined) primaryPartnerName = partnerName;
        } catch (envError) {
          if (env === HOST_ENV) throw envError;
          console.error(`[onboarding] provisioning in '${env}' failed — continuing without it:`, envError);
          skippedEnvs.push(env);
        }
      }
      if (!primaryPartnerName) throw new Error('No environment is configured for provisioning.');

      app.signIn({ partnerName: primaryPartnerName, companyName: v.legalName, email, partner: 'self-serve' });
      // Suppress the already-onboarded bounce before flipping onboarded: this user just finished the wizard
      // and must land on the 'done' screen, not be yanked to /dashboard by the reactive bounce effect.
      // Armed before refreshMe(), since that call itself may flip `onboarded` to true.
      bouncedRef.current = true;
      // Pull the now-provisioned partner's `/me` (allowMultipleBusinesses, operational, ...) into state —
      // it was last loaded at boot, before the partner/entity existed, and nothing else refreshes it.
      // Best-effort: provisioning itself already succeeded, so don't fail the wizard over this refetch.
      try {
        await app.refreshMe();
      } catch (refreshErr) {
        console.error('[onboarding] refreshMe after provisioning failed — dashboard may need a manual reload:', refreshErr);
      }
      app.markOnboarded();
      goStep('done');
    } catch (ex: any) {
      const message = ex?.message || 'We could not finish setting up your account. Please try again.';
      const unexpected = !isClientError(ex) && !KNOWN_SUBMIT_ERRORS.has(message);
      // `reason`/`status` come from the thrown error, never form data — see the "never carry ... a
      // value typed into the onboarding forms" rule in lib/analytics.ts.
      trackEvent('onboarding', 'Submission Failed', { reason: message, status: ex?.status, phase: submitPhase || undefined });
      // Payouts rejected one specific field: take the user back to the step it was entered on and mark
      // it there, rather than leave them on the summary reading a raw API message.
      const fieldErr = entityFieldErrorFrom(ex);
      if (fieldErr) {
        showFieldError(fieldErr);
        return;
      }
      setSubmitErr(message);
      setSubmitErrUnexpected(unexpected);
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
      setSubmitPhase('');
    }
  };
  // Sends the user back to the step a rejected field was entered on, and marks the field there.
  const showFieldError = ({ field, label, hint }: EntityFieldError) => {
    // With "same as legal" ticked the operating address *is* the legal one, so that's where to fix it.
    const target = field.startsWith('address.') && getValues().operatingSameAsLegal ? field.replace(/^address\./, 'legalAddress.') : field;
    if (target.startsWith('legalAddress.')) goStep('address');
    else if (target.startsWith('address.')) {
      setStep('address');
      goAddressSub(1);
    } else if (target.startsWith('owner')) goStep('owner');
    else if (['businessType', 'taxIdType', 'taxId'].includes(target)) goStep('legal');
    else goStep('business');
    setError(target, { type: 'server', message: hint });
    setSubmitErr(`We couldn’t accept “${label}”: ${hint}. Fix it, then continue to submit again.`);
  };

  const onSubmitFromReview = () => {
    clickStep('review', 'submit');
    createPartner();
  };

  // Browser Back/Forward landed on another wizard entry: show the step it recorded. Our own
  // recordPosition pushes arrive here too, already matching the current step, and are skipped.
  const positionRef = useRef({ step, addressSub });
  positionRef.current = { step, addressSub };
  useEffect(() => {
    const target = location.state as { onboardingStep?: Step; addressSub?: number } | null;
    if (!target?.onboardingStep) return;
    const current = positionRef.current;
    const targetSub = target.addressSub ?? 0;
    if (target.onboardingStep === current.step && targetSub === current.addressSub) return;
    // The account is created — the wizard behind it is spent, so leave for the dashboard instead.
    if (current.step === 'done') {
      app.markOnboarded();
      navigate('/dashboard', { replace: true });
      return;
    }
    // Don't swap the review step out from under a submission in flight.
    if (submittingRef.current) {
      recordPosition(current.step, current.addressSub);
      return;
    }
    if (target.onboardingStep !== 'done') viewStep(stepKeyFor(target.onboardingStep, targetSub));
    setStep(target.onboardingStep);
    setAddressSub(targetSub);
    setSubmitErr('');
    setSubmitErrUnexpected(false);
    stepAttemptedRef.current = false;
  }, [location.key]);

  const milestones = [
    { label: 'How you’ll use the API', sub: 'Your use case', steps: ['usecase'] },
    { label: 'Business owner', sub: 'Who owns the business', steps: ['owner'] },
    { label: 'Business profile', sub: 'Name, industry & contact', steps: ['business'] },
    { label: 'Business type & tax', sub: 'Formation & tax ID', steps: ['legal'] },
    { label: 'Addresses', sub: 'Legal & operating', steps: ['address'] },
    { label: 'Review & create', sub: 'Confirm and submit', steps: ['review'] },
  ];
  const activeMilestone = step === 'done' ? milestones.length : milestones.findIndex((m) => m.steps.includes(step));

  // Enter key: advance the current step without clicking Continue.
  const makeKeyDown = (advanceFn: () => void) => (e: KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Enter' || e.shiftKey) return;
    const target = e.target as HTMLElement;
    const tag = target.tagName;
    if (tag === 'BUTTON' || tag === 'A' || tag === 'TEXTAREA' || tag === 'SELECT') return;
    if (target.closest('[role="listbox"]') || target.closest('[role="option"]') || target.closest('[role="combobox"]')) return;
    e.preventDefault();
    advanceFn();
  };

  const v = getValues();

  return (
    <div style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      {/* Top bar */}
      <div
        style={{
          height: 64,
          borderBottom: '1px solid var(--border-static)',
          display: 'flex',
          alignItems: 'center',
          padding: '0 28px',
          flexShrink: 0,
        }}
      >
        <button
          onClick={() => go('/')}
          style={{ display: 'flex', alignItems: 'center', gap: 10, background: 'none', border: 'none', cursor: 'pointer' }}
        >
          <MelioLogo height={22} />
          <span style={{ width: 1, height: 18, background: 'var(--border-static)' }} />
          <Text textStyle="body3Semi">API</Text>
        </button>
        <div style={{ flex: 1 }} />
      </div>

      <div className="grid-2" style={{ flex: 1, gridTemplateColumns: '0.85fr 1.15fr', gap: 0, alignItems: 'stretch' }}>
        {/* Left brand + milestone panel */}
        <div
          style={{
            background: 'linear-gradient(155deg, #1c1428, #16171f 70%)',
            padding: '56px 48px',
            color: 'var(--text-inverse)',
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <div
            style={{
              position: 'absolute',
              left: -80,
              bottom: -80,
              width: 300,
              height: 300,
              borderRadius: '50%',
              background: 'radial-gradient(circle, rgba(120,73,255,.35), transparent 70%)',
            }}
          />
          <div style={{ position: 'relative', maxWidth: 360 }}>
            <Container paddingBottom="s">
              <Text as="h1" textStyle="display2Semi" color="semantic.text.inverse">
                Start moving money in minutes
              </Text>
            </Container>
            <Container paddingBottom="xxl">
              <Text textStyle="body2" color="global.neutral.700">
                Tell us a little about you and your business, and we&apos;ll create your partner and working API keys. You&apos;ll land in
                test mode.
              </Text>
            </Container>
            <div
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.08)',
                borderRadius: 14,
                padding: 24,
              }}
            >
              {/* Kept as hand-rolled markup rather than penny's <Tracker>: this rail sits on the dark
                  brand panel, and penny's Tracker is themed for light surfaces — its vertical variant
                  draws a light halo behind each indicator and gives the titles dark semantic colors,
                  neither of which is overridable. It also sizes each step to its content, which
                  overflowed this fixed-height panel. Mirrors welcome.tsx's <MilestoneRail>. */}
              {milestones.map((s, i) => {
                const done = activeMilestone > i,
                  active = activeMilestone === i;
                return (
                  <div key={i} style={{ display: 'flex', gap: 14, opacity: done || active ? 1 : 0.5 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                      <div
                        style={{
                          width: 28,
                          height: 28,
                          borderRadius: '50%',
                          flexShrink: 0,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          background: done ? '#8fd6a8' : active ? 'var(--accent)' : 'rgba(255,255,255,0.1)',
                          border: active ? '2px solid var(--accent)' : 'none',
                        }}
                      >
                        {done ? (
                          <Icon name="checked" size={14} color="#16171f" />
                        ) : (
                          <span style={{ fontSize: 12, fontWeight: 700, color: '#fff' }}>{i + 1}</span>
                        )}
                      </div>
                      {i < milestones.length - 1 && (
                        <div
                          style={{
                            width: 2,
                            flex: 1,
                            minHeight: 22,
                            background: done ? '#8fd6a8' : 'rgba(255,255,255,0.12)',
                            margin: '3px 0',
                          }}
                        />
                      )}
                    </div>
                    <div style={{ paddingBottom: 18 }}>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#fff' }}>{s.label}</div>
                      <div style={{ fontSize: 12, color: '#9aa3b2', marginTop: 1 }}>{s.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div style={{ position: 'fixed', left: 20, bottom: 20, zIndex: 10, display: 'flex', flexDirection: 'column', gap: 8 }}>
            {app.email && (
              <Group variant="horizontal" spacing="xxs" alignItems="center">
                <Icon name="user" size={14} color="#9aa3b2" />
                <Text
                  textStyle="caption1"
                  color="global.neutral.700"
                  style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: 220 }}
                >
                  {app.email}
                </Text>
              </Group>
            )}
            {/* penny-backed: a secondary-inverse <Button>, which is penny's variant for a control
                sitting on a dark surface. Replaces a hand-rolled button that swapped its own
                colors through onMouseEnter/onMouseLeave handlers. */}
            <div style={{ alignSelf: 'flex-start' }}>
              <PennyButton
                size="small"
                variant="secondary-inverse"
                label="Log out"
                leftElement={<Icon name="exit" size={16} />}
                onClick={() => app.signOut()}
              />
            </div>
          </div>
        </div>

        {/* Right form panel — a column so the legal notice can sit at its foot (marginTop:auto)
            on short steps and simply follow the form on long ones. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            padding: '56px 48px',
            overflowY: 'auto',
          }}
        >
          <div style={{ width: '100%', maxWidth: 460 }}>
            {/* ── Step: usecase (beta application) ── */}
            {step === 'usecase' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(onContinueUsecase)}>
                <StepHeader title={<>How will you use the API?</>}>
                  Tell us what you're building. We review every application to keep the Melio API secure.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('buildingFor')}
                    labelProps={{ label: 'Which describes your integration? *' }}
                    render={(props: any) => (
                      <SelectNew
                        {...props}
                        options={buildingForOptions}
                        optionRenderer={(option: any) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
                            <Text textStyle="body3Semi">{option.label}</Text>
                            {option.description && (
                              <Text textStyle="body4" color="global.neutral.900">
                                {option.description}
                              </Text>
                            )}
                          </div>
                        )}
                        placeholder="Select an option"
                      />
                    )}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('projectDescription')}
                    labelProps={{ label: 'What are you building? *' }}
                    helperText="Describe your project and how it will use Melio's Payments API."
                    render={(props: any) => (
                      <TextArea
                        {...props}
                        numberOfRows={4}
                        placeholder="e.g. A vertical SaaS that pays out marketplace sellers on our platform."
                      />
                    )}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={onContinueUsecase}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: owner (business owner + point of contact) ── */}
            {step === 'owner' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(onContinueFromOwner)}>
                <StepHeader title={<>Business owner</>}>
                  The primary individual who owns or controls the business — we're required to verify their identity. This is also the
                  account's point of contact.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    isRequired
                    {...registerField('ownerFirst')}
                    labelProps={{ label: 'First name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Jane" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('ownerLast')}
                    labelProps={{ label: 'Last name *' }}
                    render={(props: any) => <TextField {...props} placeholder="Doe" />}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('ownerEmail')}
                    labelProps={{ label: 'Email *' }}
                    render={(props: any) => <TextField {...props} type="email" placeholder="jane.doe@sunrisebakery.co" />}
                  />
                  <Form.DateField
                    {...registerField('ownerDob')}
                    colSpan={2}
                    isRequired
                    labelProps={{ label: 'Date of birth' }}
                    isTypable
                    format="MM/dd/yyyy"
                    placeholder="MM/DD/YYYY"
                    weekDays={[0, 1, 2, 3, 4, 5, 6]}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('ownerPhone')}
                    labelProps={{ label: 'Phone number *' }}
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={onBackFromOwner}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={onContinueFromOwner}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: business (profile) ── */}
            {step === 'business' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(onContinueFromBusiness)}>
                <StepHeader title={<>Tell us about your business</>}>
                  Provide the name of your business and the industry it operates in.
                </StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('legalName')}
                    labelProps={{ label: 'Business name *' }}
                    helperText="How your business appears in Melio"
                    render={(props: any) => <TextField {...props} placeholder="Sunrise Bakery LLC" />}
                  />
                  <IndustryField
                    form={form}
                    env={app.env}
                    name="industry"
                    label="Business industry"
                    isRequired
                    colSpan={2}
                    getToken={app.getAccessToken}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('phone')}
                    labelProps={{ label: 'Business phone number *' }}
                    render={(props: any) => <PhoneField {...props} placeholder="(###) ###-####" />}
                  />
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('website')}
                    labelProps={{ label: 'Business website *' }}
                    helperText="Shown to your customers in emails from us."
                    render={(props: any) => <TextField {...props} placeholder="https://sunrisebakery.co" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 16 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={onBackFromBusiness}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={onContinueFromBusiness}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: legal (business type & tax) ── */}
            {step === 'legal' && (
              <div style={{ animation: 'om-fade-up .3s ease' }} onKeyDown={makeKeyDown(onContinueFromLegal)}>
                <StepHeader title={<>Legal information</>}>The legal entity money is paid from — its formation type and tax ID.</StepHeader>
                <Form {...formProps} columns={2}>
                  <FormField
                    colSpan={2}
                    isRequired
                    {...registerField('businessType')}
                    labelProps={{ label: 'Business type *' }}
                    render={(props: any) => (
                      <SelectNew
                        {...props}
                        options={businessTypeOptions}
                        optionRenderer={(option: any) => (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 4, padding: '4px 0' }}>
                            <Text textStyle="body3Semi">{option.label}</Text>
                            {option.description && (
                              <Text textStyle="body4" color="global.neutral.900">
                                {option.description}
                              </Text>
                            )}
                          </div>
                        )}
                        placeholder="Select a type"
                      />
                    )}
                  />
                  <FormField
                    isRequired
                    {...registerField('taxIdType')}
                    labelProps={{ label: 'Tax ID type *' }}
                    render={(props: any) => <SelectNew {...props} options={taxTypeOptions} placeholder="Select" />}
                  />
                  <FormField
                    isRequired
                    {...registerField('taxId')}
                    labelProps={{ label: `${taxTypeLabels[taxIdType] || 'Tax ID'} *` }}
                    render={(props: any) => (
                      <TextField
                        {...props}
                        maskProps={{ mask: taxIdMaskFor(taxIdType), guide: false }}
                        placeholder={taxIdType === 'ein' ? '84-3920481' : '123-45-6789'}
                      />
                    )}
                  />
                  <FormField
                    colSpan={2}
                    {...registerField('description')}
                    labelProps={{ label: 'Business description' }}
                    render={(props: any) => <TextField {...props} placeholder="What your business does (optional)" />}
                  />
                </Form>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={onBackFromLegal}>
                    Back
                  </Button>
                  <Button fullWidth size="large" rightIcon="arrow-right" onClick={onContinueFromLegal}>
                    Continue
                  </Button>
                </div>
              </div>
            )}

            {/* ── Step: address (legal + operating, inner sub-step) ── */}
            {step === 'address' && (
              <div
                style={{ animation: 'om-fade-up .3s ease' }}
                onKeyDown={makeKeyDown(addressSub === 0 ? onContinueFromAddressLegal : onContinueFromAddressOperating)}
              >
                {/* Sub 0: legal registration address */}
                {addressSub === 0 && (
                  <>
                    <StepHeader title={<>Legal registration address</>}>
                      The address the business is registered at. Start typing to autocomplete.
                    </StepHeader>
                    <Form {...formProps} columns={2}>
                      <AddressFields
                        form={form}
                        env={app.env}
                        parentFieldName="legalAddress"
                        label="Legal address *"
                        getToken={app.getAccessToken}
                      />
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={onBackFromAddressLegal}>
                        Back
                      </Button>
                      <Button fullWidth size="large" rightIcon="arrow-right" onClick={onContinueFromAddressLegal}>
                        Continue
                      </Button>
                    </div>
                  </>
                )}

                {/* Sub 1: operating address */}
                {addressSub === 1 && (
                  <>
                    <StepHeader title={<>Operating address</>}>
                      Where the business operates day to day. Start typing to autocomplete.
                    </StepHeader>
                    <Form {...formProps} columns={2}>
                      <FormField
                        colSpan={2}
                        labelProps={{ label: 'Operating address', isHidden: true }}
                        render={() => (
                          <Checkbox
                            value={operatingSameAsLegal}
                            onChange={(checked: boolean) => setValue('operatingSameAsLegal', checked)}
                            label="Same as legal registration address"
                          />
                        )}
                      />
                      {!operatingSameAsLegal && (
                        <AddressFields
                          form={form}
                          env={app.env}
                          parentFieldName="address"
                          label="Operating address *"
                          getToken={app.getAccessToken}
                        />
                      )}
                    </Form>
                    {submitErr && (
                      <Banner kind="critical" style={{ marginTop: 18 }}>
                        {submitErr}
                      </Banner>
                    )}
                    <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                      <Button variant="tertiary" size="large" leftIcon="arrow-left" onClick={onBackFromAddressOperating}>
                        Back
                      </Button>
                      <Button fullWidth size="large" rightIcon="arrow-right" onClick={onContinueFromAddressOperating}>
                        Continue
                      </Button>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* ── Step: review & create ── */}
            {step === 'review' && (
              <div style={{ animation: 'om-fade-up .3s ease' }}>
                <StepHeader title={<>Review & create</>}>One last look before you submit.</StepHeader>
                <div style={{ border: '1px solid var(--border-static)', borderRadius: 12, padding: '4px 18px 14px' }}>
                  <SummaryRow label="Business name" value={v.legalName} />
                  <SummaryRow
                    label="Business type"
                    value={businessTypeOptions.find((o) => o.value === v.businessType)?.label || v.businessType}
                  />
                  <SummaryRow label="Industry" value={v.industry?.name || v.industry?.naicsCode} />
                  <SummaryRow label="Business phone" value={normPhone(v.phone)} />
                  {v.website ? <SummaryRow label="Website" value={v.website} /> : null}
                  <SummaryRow label="Tax ID" value={`${taxTypeLabels[v.taxIdType] || v.taxIdType} · ${v.taxId}`} />
                  <SummaryRow
                    label="Legal address"
                    value={[v.legalAddress.line1, v.legalAddress.city, v.legalAddress.state, v.legalAddress.postalCode]
                      .filter(Boolean)
                      .join(', ')}
                  />
                  <SummaryRow
                    label="Operating address"
                    value={
                      v.operatingSameAsLegal
                        ? 'Same as legal'
                        : [v.address.line1, v.address.city, v.address.state, v.address.postalCode].filter(Boolean).join(', ')
                    }
                  />
                  <SummaryRow label="Owner" value={`${v.ownerFirst} ${v.ownerLast} · ${v.ownerEmail}`} />
                  <SummaryRow label="Use case" value={buildingForOptions.find((o) => o.value === v.buildingFor)?.label || v.buildingFor} />
                </div>
                {submitErr && (
                  <Banner kind="critical" style={{ marginTop: 18 }}>
                    {submitErr}
                    {submitErrUnexpected && (
                      <>
                        {' '}
                        If this keeps happening, contact us at{' '}
                        <a href={`mailto:${SUPPORT_EMAIL}`} style={{ color: 'inherit', textDecoration: 'underline' }}>
                          {SUPPORT_EMAIL}
                        </a>
                        .
                      </>
                    )}
                  </Banner>
                )}
                <div style={{ display: 'flex', gap: 12, marginTop: 26 }}>
                  <Button variant="tertiary" size="large" leftIcon="arrow-left" disabled={submitting} onClick={onBackFromReview}>
                    Back
                  </Button>
                  <Button fullWidth size="large" loading={submitting} onClick={onSubmitFromReview}>
                    Submit
                  </Button>
                </div>
                {submitting && (
                  <Container paddingTop="s">
                    {/* role=status so the phase changes are announced, not just drawn. */}
                    <div role="status" aria-live="polite">
                      <Text textStyle="body4" color="semantic.text.secondary">
                        {submitPhase || 'Setting up your account'} — this may take about a minute.
                      </Text>
                    </div>
                  </Container>
                )}
              </div>
            )}

            {/* ── Step: done ── */}
            {step === 'done' && (
              <div style={{ animation: 'om-fade-up .3s ease', textAlign: 'center' }}>
                <Illustration name="celebration" size={130} style={{ margin: '0 auto 8px' }} />
                <Text as="h2" textStyle="heading1Semi">
                  You&apos;re all set{watch('legalName') ? `, ${watch('legalName')}` : ''}
                </Text>
                <Container paddingTop="xs" paddingBottom="xs">
                  <Text textStyle="body2" color="semantic.text.secondary">
                    Your account was created successfully.
                  </Text>
                </Container>
                <Container paddingBottom="m">
                  <Text textStyle="body2" color="semantic.text.secondary">
                    While we review your details, start building your integration with the staging API. Explore the API and test as you go,
                    all in the staging environment. We&apos;ll follow up if we have questions.
                  </Text>
                </Container>
                {/* Single action: continue into the app. The simulator lives inside the dashboard, so it
                    is not offered here as a pre-dashboard step. */}
                <Button
                  fullWidth
                  size="large"
                  rightIcon="arrow-right"
                  onClick={() => {
                    app.markOnboarded();
                    go('/dashboard');
                  }}
                >
                  Open dashboard
                </Button>
              </div>
            )}
          </div>
          <div style={{ width: '100%', maxWidth: 460, marginTop: 'auto', paddingTop: 40 }}>
            <LegalNotice />
          </div>
        </div>
      </div>
    </div>
  );
}

export { Onboarding, KeyReveal, ApiKeyPanel };
